/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
/**
 * Image Generation Service
 *
 * Handles async image generation via queue-callback pattern.
 * Returns jobId immediately - use useQueueJob hook to watch for completion.
 *
 * @module imageService
 */
import { logger } from '@musedrops/shared';
import { supabase } from './supabase';

interface GenerateShowImageParams {
  showId: string;
  showSlug: string;
  showTopic?: string;
  imagePrompt: string;
}

interface GenerateStoryImageParams {
  storyId: string;
}

interface GenerateImageResponse {
  success: boolean;
  jobId?: number;
  error?: string;
}

/**
 * Start show image generation job (async - returns jobId immediately).
 * Subscribe to job completion using useQueueJob hook.
 * When complete, job output contains { path: "shows/slug/timestamp.png" }
 */
export const generateShowImage = async (
  params: GenerateShowImageParams
): Promise<GenerateImageResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('gen-show-image', {
      body: {
        showSlug: params.showSlug,
        trigger: 'manual', // UI-initiated jobs get priority
      },
    });

    if (error) {
      logger.error('Show image generation error', {
        error,
        showSlug: params.showSlug,
      });
      throw new Error(error.message || 'Failed to generate image');
    }

    // Check if the response is an error from the generation function
    if (data && typeof data === 'object' && data.success === false) {
      logger.error('Show image generation failed', {
        error: data.error,
        showSlug: params.showSlug,
      });
      throw new Error(`Image generation failed: ${data.error}`);
    }

    // Extract jobId from queue-based response
    const jobId = data?.result?.jobId;

    if (!jobId || typeof jobId !== 'number') {
      logger.error('Unable to extract jobId from response', {
        data,
        showSlug: params.showSlug,
      });
      throw new Error('Invalid response from image generation function');
    }

    logger.info('Show image generation job created', {
      jobId,
      showSlug: params.showSlug,
    });

    return {
      success: true,
      jobId,
    };
  } catch (error) {
    logger.error('Show image generation service error', {
      error,
      showSlug: params.showSlug,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Start story image generation job (async - returns jobId immediately).
 * Subscribe to job completion using useQueueJob hook.
 * When complete, job output contains { path: "stories/show-slug/timestamp.png" }
 */
export const generateStoryImage = async (
  params: GenerateStoryImageParams
): Promise<GenerateImageResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('gen-story-image', {
      body: {
        storyId: params.storyId,
        trigger: 'manual', // UI-initiated jobs get priority
      },
    });

    if (error) {
      logger.error('Story image generation error', {
        error,
        storyId: params.storyId,
      });
      throw new Error(error.message || 'Failed to generate image');
    }

    // Check if the response is an error from the generation function
    if (data && typeof data === 'object' && data.success === false) {
      logger.error('Story image generation failed', {
        error: data.error,
        storyId: params.storyId,
      });
      throw new Error(`Image generation failed: ${data.error}`);
    }

    // Extract jobId from queue-based response
    const jobId = data?.result?.jobId;

    if (!jobId || typeof jobId !== 'number') {
      logger.error('Unable to extract jobId from response', {
        data,
        storyId: params.storyId,
      });
      throw new Error('Invalid response from image generation function');
    }

    logger.info('Story image generation job created', {
      jobId,
      storyId: params.storyId,
    });

    return {
      success: true,
      jobId,
    };
  } catch (error) {
    logger.error('Story image generation service error', {
      error,
      storyId: params.storyId,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
